import { z } from "zod";
import { NarrativeArcSchema } from "./narrative-arc.js";
import { NarratorVoiceSchema } from "./narrator-expression.js";
import { MechanicalNoteSchema, ProseSchema } from "./prose.js";
import { LeadSchema, OpeningSchema, SceneryFactSchema } from "./lead.js";
import { ConsequenceTypeSchema, DispositionSchema, DocumentKindSchema, FactIdSchema, ForthcomingSchema, ResistanceFormSchema, SpeakingSettingSchema, FactTierSchema, MoveTypeSchema, NpcPronounsSchema, NpcRelationshipSchema, ReliabilitySchema, MECHANISM_FAMILY_COUNT, PoliticsSchema, PressureContextSchema, RubricDimensionSchema, SizeProfileSchema, TrackSchema, } from "./primitives.js";
/**
 * P1 Case Forge output (docs/prompt_pack.md P1), extended per this task and
 * ADR 0003 / ADR 0007. CaseForge was removed on 2026-10-28: the authoring
 * relay (docs/case-authoring/) now writes what the notes below credit to it.
 *
 * NOTE (docs/RECONCILIATION.md) — two deliberate renames relative to P1's
 * literal OUTPUT JSON, both directed explicitly by this task's own field
 * list rather than invented:
 *   - P1's `decoy` object is `decoy_thesis` here.
 *   - P1's npc `private_goal` is `private_agenda` here.
 * All other P1 fields (evidence_routes, contested_pairs, calculations,
 * excellent_play_looks_like, template_driven_play_looks_like) are kept
 * unchanged — the task's extension list was additive, not an exhaustive
 * replacement of P1's schema.
 */
const CaseBibleFactSchema = z.strictObject({
    id: FactIdSchema,
    text: ProseSchema,
    tier: FactTierSchema,
    reliability: ReliabilitySchema,
    obtainable_via: z.array(z.string().min(1)).min(1),
    param_rule: z.string().min(1),
    /** ADR 0003: which authored documents this fact is located inside. */
    located_in_documents: z.array(z.string().min(1)),
    /**
     * Additive field (docs/RECONCILIATION.md), needed by packages/documents
     * section 4's buildForcedRouteDocument: CLAUDE.md Law 2 requires that when
     * the engine force-opens a route to honour the core-fact guarantee, the
     * hint is "delivered in-fiction through an NPC... never as a system
     * message" and "nudges toward the thread without stating the conclusion."
     * That hint has to be authored per fact by CaseForge, not generated at
     * play time — packages/documents is pure and never invents narrative
     * content. Null for facts that never need a forced route (non-CORE
     * facts, or CORE facts already reachable without ever needing one).
     */
    forced_route_hint: ProseSchema.nullable(),
    /**
     * Additive field (docs/RECONCILIATION.md, Harborlight content phase,
     * task section 5): "per-fact HINT PAYLOADS for the invariant channel...
     * in three variants — mentor voice, ally voice, and self-postit voice."
     * Deliberately separate from `forced_route_hint` above rather than
     * replacing it — packages/documents' buildForcedRouteDocument (already
     * built and verified) reads `forced_route_hint` as a single string, and
     * changing its shape would require touching packages/documents, which
     * this phase's task explicitly says not to do. This field is additive,
     * authored content for consumers that want the full voice set.
     */
    hint_voices: z
        .strictObject({ mentor_voice: ProseSchema, ally_voice: ProseSchema, self_postit_voice: ProseSchema })
        .nullable(),
});
const CaseBibleDocumentSchema = z.strictObject({
    doc_id: z.string().min(1),
    kind: DocumentKindSchema,
    authored_by_npc: z.string().min(1),
    contains_facts: z.array(FactIdSchema),
    unlock_channels: z.array(z.string().min(1)),
    /**
     * Additive field, not in this task's literal CaseBibleDocument field list
     * (docs/RECONCILIATION.md) — needed because packages/documents section 3's
     * resolveRequest is required to match "bible-declared aliases" (e.g. a
     * learner typing "the P&L" or "clinic financials" for a document titled
     * "Clinic-Level P&L, Trailing Two Quarters"), and nothing in the existing
     * schema carried that alias list. Deliberately authored per-document by
     * CaseForge rather than derived — an alias is a judgment call about how a
     * learner might plausibly refer to a document, not something inferrable
     * from its title alone.
     */
    aliases: z.array(z.string().min(1)),
    /** An artifact created by an admitted story development or test, not pre-existing evidence. */
    narrative_gate: z.literal(true).optional(),
    /**
     * P1's raw authored prose for this document. This is CaseForge's authoring
     * output — a looser, single-blob shape than the strict per-kind
     * DocumentSchema in document.ts, which a later validation/restructuring
     * step (case:validate, a future phase) produces from this. Still
     * Prose-typed: Law 2 bans mechanical vocabulary from authored content
     * regardless of pipeline stage.
     */
    body: ProseSchema,
    /** Presentation only. Never contains findings or a prescribed interpretation. */
    narrative: z.strictObject({
        arrival: ProseSchema,
        revisit: ProseSchema,
        decision: ProseSchema,
    }).optional(),
});
const CaseBibleContestedPairSchema = z.strictObject({
    fact_a: FactIdSchema,
    fact_b: FactIdSchema,
    resolution: ProseSchema,
    what_it_teaches: MechanicalNoteSchema,
});
/**
 * MANNER (ADR 0020, idea 4): a typed summary of the prose every NPC sheet already carries in
 * `volunteers`, `voice_notes` and `distorts`, so the engine can read what the Adjudicator
 * reads and the Narrator has never seen. How forthcoming a person is, is not how warm they
 * are: Owen Ruttle volunteers his grievance at length from HOSTILE; Sunny Petrakis answers
 * precisely what is asked and stops from NEUTRAL. Two resistance forms at most (Dunham: a
 * trait the learner cannot notice in two exchanges is noise). `offers` are documents this
 * person authored and would volunteer on a first meeting; they add a route and never replace
 * one. `tells` are Codex's lines for the person beat, empty until authored.
 */
export const NpcMannerSchema = z.strictObject({
    forthcoming: ForthcomingSchema,
    resistance: z.array(ResistanceFormSchema).max(2),
    setting: SpeakingSettingSchema,
    offers: z.array(z.string().min(1)).max(2).default([]),
    tells: z
        .strictObject({ open: z.array(ProseSchema).max(3), resisting: z.array(ProseSchema).max(3) })
        .default({ open: [], resisting: [] }),
    /**
     * What this person wants and fears, as facts (stage 3; idea 5). A turn that puts a
     * `pleased_by` fact in front of them is one more positive person term; a `threatened_by`
     * fact, one more negative term with its exit. Claude derives both from `private_agenda`.
     * Engine-only: `manner` is stripped from every model view.
     */
    agenda: z.strictObject({ pleased_by: z.array(FactIdSchema).max(3), threatened_by: z.array(FactIdSchema).max(3) }).optional(),
});
const CaseBibleNpcSchema = z.strictObject({
    name: z.string().min(1),
    role: z.string().min(1),
    /**
     * 19C (2026-10-28): she, he or they, read from this sheet's own prose. Optional so every
     * published edition resolves; case:check V64 refuses a bible without it. Reaches the narrator
     * view (as "she/her") and the adjudicator's case view; the learner view leaves it out.
     */
    pronouns: NpcPronounsSchema.optional(),
    relationship: NpcRelationshipSchema,
    starting_disposition: DispositionSchema,
    private_agenda: ProseSchema,
    volunteers: ProseSchema,
    reveals_only_if_asked: ProseSchema,
    distorts: ProseSchema,
    voice_notes: MechanicalNoteSchema,
    narrator_voice: NarratorVoiceSchema.optional(),
    /**
     * AUTHORED disposition tells (ADR 0009's authored-tells rule): the
     * in-fiction lines that surface when this NPC's disposition shifts,
     * written in the case's own voice. Replaces the generic synthesized
     * strings that used to live in apps/api's SSE layer — a tell is case
     * writing, not renderer code. Multiple variants rotate deterministically.
     * Empty arrays fall back to a flagged generic line (never silently).
     */
    disposition_tells: z
        .strictObject({
        warming: z.array(ProseSchema),
        cooling: z.array(ProseSchema),
    })
        .default({ warming: [], cooling: [] }),
    /** ADR 0020, idea 4. Optional so every published edition resolves; required by case:check once the retrofit closes. */
    manner: NpcMannerSchema.optional(),
});
const CaseBibleCalculationSchema = z.strictObject({
    prompt_to_learner: ProseSchema,
    method: MechanicalNoteSchema,
    answer_rule: MechanicalNoteSchema,
    common_error: MechanicalNoteSchema,
    /**
     * Optional machine-checkable form of `answer_rule` in the param-rule
     * grammar (docs/case-authoring/05 §1.3), evaluated by case:check against
     * the rendered tables. A tolerance-bearing answer_rule with no
     * calculation_rule is a WARNING (V23).
     */
    calculation_rule: z.string().min(1).optional(),
});
/**
 * An AUTHORED diegetic tell at a clock threshold (ADR 0009). The environment
 * section renders exactly these tells — a binding with no authored source is
 * a defect — so a clock state with no tell here has no visual form, by
 * authorial decision rather than renderer improvisation.
 */
/**
 * The binding tokens the environment section knows how to render (ADR 0009;
 * packages/environment/src/project/project.ts declares the bindings). Owned
 * here because contracts owns closed vocabularies and packages/documents may
 * import only contracts: case:check asserts every clock tell's `visual` is
 * one of these or the literal PROSE_ONLY_VISUAL. Adding a binding means
 * adding its token here in the same change.
 */
export const VISUAL_TOKENS = ["calendar", "window-light", "interview-posture", "sponsor-station", "pinboard", "bullpen-chairs"];
export const PROSE_ONLY_VISUAL = "prose-only";
/**
 * The case FORMAT version — the vocabulary set the bible schema expresses
 * (docs/case-authoring/01 §0). Distinct from a case's own `version`. The
 * bundle builder stamps it on bundle.json and case:check refuses a bundle
 * from a different major. 1.1.0: track + library metadata fields added.
 */
export const CASE_FORMAT_VERSION = "1.1.0";
const ClockThresholdTellSchema = z.strictObject({
    /** Fill level (1..size) at which this tell becomes the active one. */
    at: z.number().int().min(1),
    /** The authored diegetic prose for this level, in the case's voice. */
    tell: ProseSchema,
    /**
     * The visual channel that renders this tell in the environment section
     * (a binding token, e.g. "calendar", "bullpen-chairs", "sponsor-station",
     * "interview-posture", "pinboard"), or the literal "prose-only" for a
     * tell that deliberately has no physical form. case:check asserts every
     * Calibration clock authors its tells.
     */
    visual: z.string().regex(/^[a-z][a-z0-9-]*$/),
});
const CaseBibleClockSchema = z.strictObject({
    name: z.string().min(1),
    /**
     * What the clock IS to the engine, independent of what the case calls it.
     * "answer_confidence" marks the one clock that gates the recommendation
     * (the engine's presentation threshold fills it from evidence earned and
     * refuses to present until it reaches 4). Every case must have exactly one
     * — case:check enforces it — and it may be named anything in the case's
     * own voice ("What You Can Defend", "The Weight of the Answer"). Absent on
     * a clock literally named "Answer Confidence" for backwards compatibility.
     *
     * "deadline" marks the engagement's day clock (2026-09-17). Optional: the
     * engine otherwise takes the one clock sized to `resources.time_days_left`.
     */
    role: z.enum(["answer_confidence", "deadline"]).optional(),
    size: z.number().int().min(1),
    fills_when: MechanicalNoteSchema,
    consequence_when_full: MechanicalNoteSchema,
    /** ADR 0009: authored per-threshold tells. Sorted ascending by `at`; each `at` <= size. */
    threshold_tells: z.array(ClockThresholdTellSchema).default([]),
    /**
     * ADR 0020, idea 5: the client person this clock is about ("CFO Trust" is Renata's), so
     * the engine can read a clock most of the way full into that person's state. Read-only:
     * the person state never ticks a clock, and a clock never moves the disposition ladder.
     * Never the mentor, Doris, the deadline or the answer-confidence clock (case:check).
     */
    person: z.string().min(1).optional(),
});
const CaseBibleResourcesSchema = z.strictObject({
    time_days_left: z.number().int().min(1),
    political_capital: z.number().int().min(0),
    analysts: z.number().int().min(0),
});
const CaseBibleEvidenceRouteSchema = z.strictObject({
    conclusion: MechanicalNoteSchema,
    /** THREE ROUTES RULE (P1 constraint 2): at least three independent paths. */
    routes: z.array(MechanicalNoteSchema).min(3),
    /**
     * Optional, one entry per route in the same order: the doc_ids whose
     * yield covers that route, so case:check's solvability pass (V25,
     * docs/case-authoring/05 §1.4) can prove each authored route is actually
     * reachable rather than trusting the prose. Absent = WARNING, never an
     * error, so existing bibles stay valid.
     */
    route_doc_ids: z.array(z.array(z.string().min(1))).optional(),
});
const CaseBibleBeatSchema = z.strictObject({
    id: z.string().min(1),
    title: z.string().min(1),
    opening_narration: ProseSchema,
});
const CaseBibleCounterfactualSchema = z.strictObject({
    thread: z.string().min(1),
    reveal: ProseSchema,
});
const WORD_COUNT_LIMIT = 120;
function countWords(text) {
    return text.trim().split(/\s+/).filter(Boolean).length;
}
/**
 * The four new top-level sections below are additive (docs/RECONCILIATION.md,
 * Harborlight content phase, task sections 5-6): CaseBibleSchema had no home
 * for authored mechanics-payload or debrief-payload content at all. Each is
 * closed over its governing enum (RubricDimension/ConsequenceType/MoveType)
 * with an exact `.length()` so authoring a case can't silently skip one.
 */
const RubricAnchorSchema = z.strictObject({
    dimension: RubricDimensionSchema,
    anchor_2: ProseSchema,
    anchor_3: ProseSchema,
    anchor_5: ProseSchema,
});
const FallbackProseEntrySchema = z.strictObject({
    consequence_type: ConsequenceTypeSchema,
    variants: z.array(ProseSchema).length(3),
});
const MoveDifficultyGuidanceSchema = z.strictObject({
    move_type: MoveTypeSchema,
    guidance: MechanicalNoteSchema,
    reason: MechanicalNoteSchema,
});
const DebriefAnalysisSchema = z.strictObject({
    highest_leverage_moments: z.array(ProseSchema).length(3),
    common_wrong_turns: z.array(ProseSchema).length(2),
    fastest_unlock_question: ProseSchema,
});
/**
 * ADR 0020, idea 1: the gated document an expensive move routes around, and the custodian
 * it goes around. When a learner asks anyone but that custodian for that document, the
 * engine appends the move's authored cost to every band of the pre-declared check.
 */
export const ExpensiveMoveRouteSchema = z.strictObject({
    doc_id: z.string().min(1),
    custodian: z.string().min(1),
});
const ExpensiveMoveSchema = z.strictObject({
    description: ProseSchema,
    political_capital_cost: z.number().int().min(1),
    reason: MechanicalNoteSchema,
    route_around: ExpensiveMoveRouteSchema.optional(),
});
/**
 * Prologue pages (story-legibility phase, step 1; design_frames.pdf frames
 * 9-10). AUTHORED, human-written story pages rendered verbatim before the
 * game interface boots — never generated, paraphrased, or LLM-touched.
 * Body text is a plain string, not Prose: this is final human copy
 * reproduced character-for-character (curly quotes, profanity, blank-line
 * rhythm), and no machine filter may alter it.
 */
const ProloguePageBlockSchema = z.strictObject({
    /**
     * `dialogue` is spoken by someone else (the mentor, usually); `learner_dialogue`
     * is the player character's own line, rendered the way the learner's own words
     * render in the play thread — right-aligned, in the soft ink — so the prologue
     * already reads as a conversation the learner is part of.
     */
    kind: z.enum(["paragraph", "dialogue", "learner_dialogue"]),
    text: z.string().min(1),
});
const ProloguePageSchema = z.strictObject({
    index: z.number().int().min(1),
    /** The eyebrow above the title — e.g. "01 /". */
    eyebrow: z.string().min(1),
    /** The case name as displayed — e.g. "Harborlight Veterinary Group". */
    title: z.string().min(1),
    /** Asset slot id for the hero image; the client resolves or falls back to a generated plate. */
    hero_image_slot: z.string().min(1),
    blocks: z.array(ProloguePageBlockSchema).min(1),
});
export const CaseBibleSchema = z.strictObject({
    case_id: z.string().min(1),
    client: z.string().min(1),
    industry: z.string().min(1),
    /**
     * Which track this case is authored for (docs/case-authoring/04 §5). It
     * selects the TRACK_DIALS the orchestrator runs the lead ladder with, and
     * the parameter column case:check measures the case against. Authored,
     * not chosen at session time: a Chaos case played from the Calibration
     * menu is still Chaos. Law 6: the dials shape emphasis and pacing only —
     * never a DC, a position, or a removed option.
     */
    track: TrackSchema,
    narrative_arc: NarrativeArcSchema.optional(),
    /**
     * Library metadata (docs/case-authoring/04 §6), authored with the case and
     * read by case:check and the library surface — never by the engine at play
     * time, and never by a model: all four are poisoned on AdjudicatorCaseView
     * because the mechanism family and its primary dimension are
     * solution-adjacent.
     */
    size_profile: SizeProfileSchema,
    /** Which of the mechanism families in docs/case-authoring/02 §2 this case instantiates. */
    mechanism: z.number().int().min(1).max(MECHANISM_FAMILY_COUNT),
    politics: PoliticsSchema,
    /** The rubric dimension this case exists to train; the library filters on it. */
    primary_dimension: RubricDimensionSchema,
    /**
     * Why the pressure is on when the case opens (docs/case-authoring/02 §7):
     * the source the deadline clock counts down to and the antagonist clocks
     * escalate from. One of the distance rule's axes.
     */
    pressure_context: PressureContextSchema,
    /**
     * Author-facing, one or two sentences: WHY the pressure rises over the
     * engagement rather than merely elapsing — what the source does when it
     * is kept waiting. The hard-deadline and antagonist clocks' threshold tells
     * must dramatise this; case:check asserts the clocks exist, this says what
     * they are for.
     */
    pressure_escalation: MechanicalNoteSchema,
    engagement_question: ProseSchema,
    opening_brief: ProseSchema.refine((value) => countWords(value) <= WORD_COUNT_LIMIT, {
        message: `opening_brief must be <= ${WORD_COUNT_LIMIT} words`,
    }),
    hidden_truth: z.strictObject({ mechanism: ProseSchema, why_non_obvious: ProseSchema }),
    decoy_thesis: z.strictObject({
        claim: ProseSchema,
        why_plausible: ProseSchema,
        what_disproves_it: ProseSchema,
        /**
         * Additive field (docs/RECONCILIATION.md), needed by packages/documents
         * section 5's checkCaseConsistency: "decoy thesis supported by >= 2 real
         * facts" is only checkable if the bible declares WHICH real facts are
         * meant to support it. Deliberately not schema-min-length-enforced here
         * (that would make the runtime check unreachable/always-true) — the
         * >= 2 threshold is enforced by checkCaseConsistency itself, as a real
         * Diagnostic an author can see and fail on.
         */
        supporting_fact_ids: z.array(FactIdSchema),
    }),
    evidence_routes: z.array(CaseBibleEvidenceRouteSchema).min(1),
    facts: z.array(CaseBibleFactSchema).min(1),
    documents: z.array(CaseBibleDocumentSchema),
    contested_pairs: z.array(CaseBibleContestedPairSchema).min(2),
    npcs: z.array(CaseBibleNpcSchema).min(3).max(9),
    calculations: z.array(CaseBibleCalculationSchema).min(2).max(3),
    clocks: z.array(CaseBibleClockSchema).min(1),
    resources: CaseBibleResourcesSchema,
    beats: z.array(CaseBibleBeatSchema).min(1),
    excellent_play_looks_like: z.array(MechanicalNoteSchema),
    template_driven_play_looks_like: z.array(MechanicalNoteSchema),
    counterfactuals: z.array(CaseBibleCounterfactualSchema).min(1),
    counterfactual_reveal: ProseSchema,
    version: z.string().regex(/^\d+\.\d+\.\d+$/, "version must be semver (e.g. 1.0.0)"),
    content_hash: z.string().min(1),
    /**
     * Additive field (docs/RECONCILIATION.md), needed by packages/documents
     * section 5's METRIC AGREEMENT check: "same metric/entity/period across
     * docs must agree unless in bible's intentional_contradictions[] registry
     * with a reason." Nothing in the existing schema carried that registry.
     */
    intentional_contradictions: z.array(z.strictObject({ metric: z.string().min(1), entity: z.string().min(1), period: z.string().min(1), reason: ProseSchema })),
    /**
     * Additive field (docs/RECONCILIATION.md), needed by packages/documents
     * section 5's TEMPORAL check: "no document dated after engagement's
     * present day." Nothing in the existing schema named the engagement's
     * current in-fiction date; `resources.time_days_left` is a countdown, not
     * a calendar date. ISO (YYYY-MM-DD) so it is safely comparable to any
     * document whose own `created_in_fiction_at` also happens to be ISO —
     * see check-case-consistency.ts for why non-ISO document dates are
     * skipped rather than flagged.
     */
    present_day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "present_day must be an ISO date (YYYY-MM-DD)"),
    /** Additive (task section 6): case-specific 2/3/5 anchors for every RubricDimension, so the Assessor has concrete anchors rather than abstractions. */
    rubric_anchors: z.array(RubricAnchorSchema).length(6),
    /** Additive (task section 5): 3 authored variants per ConsequenceType — what ships when the Narrator gate rejects a generation twice. */
    fallback_prose: z.array(FallbackProseEntrySchema).length(7),
    /** Additive (task section 5): per-MoveType difficulty guidance with authored reasons, fed to the Adjudicator as case context. */
    move_difficulty_guidance: z.array(MoveDifficultyGuidanceSchema).length(6),
    /** Additive (task section 6): the debrief-payload analysis — highest-leverage moments, common wrong turns, fastest-unlock question. */
    debrief_analysis: DebriefAnalysisSchema,
    /** Additive (task section 5): the authored political-capital cost of each expensive move named in the case. */
    expensive_moves: z.array(ExpensiveMoveSchema).min(1),
    /** Story-legibility phase, step 1: the authored prologue pages (frames 9-10), rendered verbatim before play. */
    prologue_pages: z.array(ProloguePageSchema).min(1),
    /**
     * Story-legibility phase, step 2: the documents already on the desk when
     * the game interface boots — delivered as ordinary DOCUMENT_DELIVERED
     * events at turn 0 during session start, never a client-side special case.
     */
    opening_desk_document_ids: z.array(z.string().min(1)).min(1),
    /**
     * Structure-and-rewards phase: the authored opening paragraph — the first
     * words of the game, emitted as turn-0 narration in place of per-document
     * arrival lines. Authored to plant promises; case:check verifies the two
     * planted promise ids exist.
     */
    opening_scene: z.strictObject({
        text: ProseSchema,
        /** Promise ids this paragraph surfaces at turn 0. */
        plants: z.array(z.string().min(1)).min(2),
        /**
         * Where the engagement opens. "client_site" withholds the consultants' office from the
         * narrator's staging (rooms, palette, cast) until a case scene is active; the illustrated
         * office still draws. Absent means "office" (2026-09-15 readthrough, item 5).
         */
        stage: z.enum(["office", "client_site"]).optional(),
    }),
    /**
     * THE SELF POST-IT — a per-case convention, not a Harborlight one-off.
     *
     * Every case authors its own note-to-self: different content, same job. It
     * names the four things a learner can do, lists who is who, and implies that
     * an attempt can go badly and be come at another way. It opens once, on first
     * entry, and never again.
     *
     * It lives in the bible rather than being recognised by a magic doc id, so
     * `pnpm case:check` can fail an author who forgets one — which is what makes
     * it a convention rather than a habit. The named document must be a POSTIT
     * authored by "self", must be on the opening desk, and must carry both a cast
     * list and an aside; check-orientation-note.ts enforces all of that.
     */
    orientation_note_doc_id: z.string().min(1),
    /** The authored lead set (ADR 0008). */
    leads: z.array(LeadSchema).min(1),
    /** Chekhov registry: facts that are deliberately scenery, with reasons. */
    scenery_facts: z.array(SceneryFactSchema),
    /** Authored openings (stage 3). Optional so every published edition resolves; never in a model view. */
    openings: z.array(OpeningSchema).optional(),
});
//# sourceMappingURL=case-bible.js.map